import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

/**
 * 化学实验室 - 横屏「分子可视化」风格
 *
 * 核心亮点：
 * - SVG 分子结构旋转动画（原子 + 化学键）
 * - 化学方程式逐步展示
 * - 元素周期表片段高亮
 * - 实验数据面板
 * - 反应条件标注
 *
 * 尺寸：1920x1080 横屏
 */

interface Atom {
  symbol: string;
  color: string;
  x: number;
  y: number;
  r: number;
}

interface Bond {
  from: number;
  to: number;
  type: 'single' | 'double';
}

interface ElementInfo {
  symbol: string;
  name: string;
  number: number;
  mass: string;
  color: string;
}

interface ChemistryLabTemplateProps {
  topic: string;
  subtitle: string;
  atoms: Atom[];
  bonds: Bond[];
  equation: { left: string; right: string; condition: string };
  elements: ElementInfo[];
  properties: { label: string; value: string; icon: string }[];
  keyFact: string;
  applications: string[];
}

const DEFAULT_PROPS: ChemistryLabTemplateProps = {
  topic: '水分子结构与性质',
  subtitle: 'H₂O Molecular Structure & Properties',
  atoms: [
    { symbol: 'O', color: '#ef4444', x: 300, y: 260, r: 36 },
    { symbol: 'H', color: '#3b82f6', x: 180, y: 360, r: 26 },
    { symbol: 'H', color: '#3b82f6', x: 420, y: 360, r: 26 },
  ],
  bonds: [
    { from: 0, to: 1, type: 'single' },
    { from: 0, to: 2, type: 'single' },
  ],
  equation: {
    left: '2H₂ + O₂',
    right: '2H₂O',
    condition: '点燃',
  },
  elements: [
    { symbol: 'H', name: '氢', number: 1, mass: '1.008', color: '#3b82f6' },
    { symbol: 'O', name: '氧', number: 8, mass: '15.999', color: '#ef4444' },
  ],
  properties: [
    { label: '键角', value: '104.5°', icon: '📐' },
    { label: '分子量', value: '18.015 g/mol', icon: '⚖️' },
    { label: '沸点', value: '100°C', icon: '🌡️' },
    { label: '密度', value: '1.0 g/cm³', icon: '💧' },
  ],
  keyFact: '水的反常膨胀使冰浮在水面，这一特性保护了水下生物在寒冬中存活',
  applications: [
    '生命活动的溶剂与载体',
    '化学反应的重要介质',
    '工业冷却与能源传输',
    '地球气候调节的关键',
  ],
};

export const ChemistryLabTemplate: React.FC<Partial<ChemistryLabTemplateProps>> = (props) => {
  const p = { ...DEFAULT_PROPS, ...props };
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const headerProgress = spring({ frame, fps, config: { damping: 18, stiffness: 90 } });

  const moleculeAppear = interpolate(frame, [20, 70], [0, 1], { extrapolateRight: 'clamp' });
  const rotAngle = frame * 0.4;

  const eqStart = 80;
  const eqProgress = interpolate(frame, [eqStart, eqStart + 50], [0, 1], { extrapolateRight: 'clamp' });
  const arrowProgress = interpolate(frame, [eqStart + 20, eqStart + 40], [0, 1], { extrapolateRight: 'clamp' });

  const elemStart = 140;
  const propStart = 200;
  const propInterval = 30;
  const factStart = propStart + p.properties.length * propInterval + 30;
  const factOpacity = interpolate(frame, [factStart, factStart + 25], [0, 1], { extrapolateRight: 'clamp' });
  const appStart = factStart + 40;

  const breathe = 1 + 0.015 * Math.sin(frame * 0.08);

  return (
    <AbsoluteFill
      style={{
        background: 'linear-gradient(160deg, #0f172a 0%, #1e1b4b 40%, #0f172a 100%)',
        fontFamily: '-apple-system, "PingFang SC", "Inter", sans-serif',
        color: 'white',
        overflow: 'hidden',
      }}
    >
      {/* Hex grid background */}
      <svg style={{ position: 'absolute', inset: 0, opacity: 0.04 }} width="1920" height="1080">
        {Array.from({ length: 12 }).map((_, row) =>
          Array.from({ length: 16 }).map((_, col) => {
            const cx = col * 130 + (row % 2) * 65;
            const cy = row * 100;
            return (
              <polygon
                key={`${row}-${col}`}
                points={Array.from({ length: 6 }).map((__, i) => {
                  const a = (Math.PI / 3) * i - Math.PI / 6;
                  return `${cx + 40 * Math.cos(a)},${cy + 40 * Math.sin(a)}`;
                }).join(' ')}
                fill="none" stroke="white" strokeWidth="0.5"
              />
            );
          })
        )}
      </svg>

      {/* Header */}
      <div style={{ position: 'absolute', top: 32, left: 60, opacity: headerProgress }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
          <div style={{ padding: '6px 18px', background: '#6366f118', border: '1px solid #6366f140', borderRadius: 10, fontSize: 22, color: '#818cf8', fontWeight: 700 }}>
            🧪 CHEMISTRY
          </div>
        </div>
        <h1 style={{ fontSize: 50, fontWeight: 900, margin: 0 }}>{p.topic}</h1>
        <p style={{ fontSize: 24, color: 'rgba(255,255,255,0.3)', marginTop: 4, fontStyle: 'italic' }}>{p.subtitle}</p>
      </div>

      {/* ========== MOLECULE STRUCTURE (left) ========== */}
      <svg style={{ position: 'absolute', left: 40, top: 160 }} width="640" height="500" viewBox="0 0 640 500">
        <g transform={`rotate(${rotAngle * 0.15}, 300, 280)`}>
          {/* Bonds */}
          {p.bonds.map((bond, i) => {
            const a1 = p.atoms[bond.from];
            const a2 = p.atoms[bond.to];
            if (!a1 || !a2) return null;
            const opacity = moleculeAppear;
            if (bond.type === 'double') {
              const dx = a2.y - a1.y;
              const dy = -(a2.x - a1.x);
              const len = Math.sqrt(dx * dx + dy * dy) || 1;
              const offset = 4;
              return (
                <g key={`bond-${i}`} opacity={opacity}>
                  <line x1={a1.x + dx / len * offset} y1={a1.y + dy / len * offset} x2={a2.x + dx / len * offset} y2={a2.y + dy / len * offset} stroke="rgba(255,255,255,0.35)" strokeWidth="3" />
                  <line x1={a1.x - dx / len * offset} y1={a1.y - dy / len * offset} x2={a2.x - dx / len * offset} y2={a2.y - dy / len * offset} stroke="rgba(255,255,255,0.35)" strokeWidth="3" />
                </g>
              );
            }
            return (
              <line key={`bond-${i}`} x1={a1.x} y1={a1.y} x2={a2.x} y2={a2.y} stroke="rgba(255,255,255,0.35)" strokeWidth="3.5" opacity={opacity} />
            );
          })}

          {/* Atoms */}
          {p.atoms.map((atom, i) => {
            const scale = moleculeAppear * breathe;
            return (
              <g key={`atom-${i}`} opacity={moleculeAppear}>
                <circle cx={atom.x} cy={atom.y} r={atom.r * scale * 1.4} fill={atom.color} opacity="0.08" />
                <circle cx={atom.x} cy={atom.y} r={atom.r * scale} fill={atom.color} opacity="0.25" />
                <circle cx={atom.x} cy={atom.y} r={atom.r * scale * 0.85} fill={`${atom.color}90`} stroke={atom.color} strokeWidth="2" />
                <text x={atom.x} y={atom.y + 8} textAnchor="middle" fill="white" fontSize={atom.r * 0.8} fontWeight="900">
                  {atom.symbol}
                </text>
              </g>
            );
          })}

          {/* Bond angle indicator */}
          <path
            d={`M${p.atoms[1]?.x},${p.atoms[1]?.y} L${p.atoms[0]?.x},${p.atoms[0]?.y} L${p.atoms[2]?.x},${p.atoms[2]?.y}`}
            fill="none" stroke="#fbbf24" strokeWidth="1.5" strokeDasharray="5 3"
            opacity={moleculeAppear * 0.4}
          />
        </g>

        {/* Electron cloud shimmer */}
        {p.atoms.map((atom, i) => {
          const shimmer = 0.06 + 0.03 * Math.sin(frame * 0.1 + i * 2);
          return (
            <circle key={`cloud-${i}`} cx={atom.x} cy={atom.y} r={atom.r * 2.2}
              fill={atom.color} opacity={shimmer * moleculeAppear}
              style={{ filter: 'blur(8px)' }}
            />
          );
        })}
      </svg>

      {/* ========== EQUATION BAR ========== */}
      <div
        style={{
          position: 'absolute',
          left: 130,
          top: 680,
          display: 'flex',
          alignItems: 'center',
          gap: 20,
          opacity: eqProgress,
        }}
      >
        <div style={{ fontSize: 36, fontWeight: 800, fontFamily: '"Times New Roman", serif', color: '#e2e8f0' }}>
          {p.equation.left}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <span style={{ fontSize: 20, color: '#fbbf24', fontWeight: 600 }}>{p.equation.condition}</span>
          <svg width={80 * arrowProgress} height="20" viewBox={`0 0 ${80} 20`}>
            <line x1="0" y1="10" x2="65" y2="10" stroke="#fbbf24" strokeWidth="2" />
            <polygon points="65,4 80,10 65,16" fill="#fbbf24" />
          </svg>
        </div>
        <div style={{ fontSize: 36, fontWeight: 800, fontFamily: '"Times New Roman", serif', color: '#22c55e' }}>
          {p.equation.right}
        </div>
      </div>

      {/* ========== RIGHT PANEL ========== */}
      <div style={{ position: 'absolute', top: 100, left: 720, right: 50 }}>
        {/* Element cards */}
        <div style={{ fontSize: 26, color: 'rgba(255,255,255,0.3)', marginBottom: 14, letterSpacing: '0.1em', fontWeight: 600 }}>组成元素</div>
        <div style={{ display: 'flex', gap: 16, marginBottom: 28 }}>
          {p.elements.map((el, i) => {
            const progress = spring({ frame: frame - elemStart - i * 20, fps, from: 0, to: 1, config: { damping: 14, stiffness: 120 } });
            return (
              <div
                key={i}
                style={{
                  width: 140,
                  padding: '18px',
                  background: `${el.color}10`,
                  border: `2px solid ${el.color}30`,
                  borderRadius: 16,
                  textAlign: 'center',
                  opacity: progress,
                  transform: `scale(${0.8 + 0.2 * progress})`,
                }}
              >
                <div style={{ fontSize: 30, color: 'rgba(255,255,255,0.3)' }}>{el.number}</div>
                <div style={{ fontSize: 44, fontWeight: 900, color: el.color, margin: '4px 0' }}>{el.symbol}</div>
                <div style={{ fontSize: 24, fontWeight: 700, color: 'rgba(255,255,255,0.7)' }}>{el.name}</div>
                <div style={{ fontSize: 20, color: 'rgba(255,255,255,0.3)', marginTop: 4 }}>{el.mass}</div>
              </div>
            );
          })}
        </div>

        {/* Properties */}
        <div style={{ fontSize: 26, color: 'rgba(255,255,255,0.3)', marginBottom: 14, letterSpacing: '0.1em', fontWeight: 600 }}>物理性质</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          {p.properties.map((prop, i) => {
            const start = propStart + i * propInterval;
            const progress = spring({ frame: frame - start, fps, from: 0, to: 1, config: { damping: 12, stiffness: 140 } });
            return (
              <div
                key={i}
                style={{
                  padding: '16px 20px',
                  background: 'rgba(255,255,255,0.03)',
                  borderRadius: 14,
                  border: '1px solid rgba(255,255,255,0.06)',
                  opacity: progress,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 14,
                }}
              >
                <span style={{ fontSize: 26 }}>{prop.icon}</span>
                <div>
                  <div style={{ fontSize: 26, color: 'rgba(255,255,255,0.35)' }}>{prop.label}</div>
                  <div style={{ fontSize: 24, fontWeight: 800, color: '#e2e8f0' }}>{prop.value}</div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Applications */}
        <div style={{ marginTop: 24 }}>
          <div style={{ fontSize: 26, color: 'rgba(255,255,255,0.3)', marginBottom: 12, fontWeight: 600 }}>🔬 应用领域</div>
          {p.applications.map((app, i) => {
            const opacity = interpolate(frame, [appStart + i * 22, appStart + i * 22 + 18], [0, 1], { extrapolateRight: 'clamp' });
            return (
              <div key={i} style={{ display: 'flex', gap: 12, marginBottom: 10, opacity }}>
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#818cf8', marginTop: 10, flexShrink: 0 }} />
                <span style={{ fontSize: 24, color: 'rgba(255,255,255,0.6)', lineHeight: 1.5 }}>{app}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Key fact */}
      <div style={{ position: 'absolute', bottom: 40, left: 60, right: 60, opacity: factOpacity }}>
        <div style={{ padding: '18px 28px', background: '#1e1b4b', border: '1px solid rgba(99,102,241,0.2)', borderRadius: 16, textAlign: 'center' }}>
          <span style={{ fontSize: 22, color: 'rgba(255,255,255,0.8)', lineHeight: 1.5 }}>💡 {p.keyFact}</span>
        </div>
      </div>

      {/* Progress */}
      <div style={{ position: 'absolute', bottom: 0, left: 0, width: `${interpolate(frame, [0, 480], [0, 100], { extrapolateRight: 'clamp' })}%`, height: 3, background: 'linear-gradient(90deg, #6366f1, #818cf8)' }} />
    </AbsoluteFill>
  );
};
